import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useMutation, useQueryClient, useQuery } from 'react-query';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  CircularProgress,
  InputAdornment,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Divider,
  useTheme,
  useMediaQuery,
  alpha,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper as MuiPaper,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  LocationOn,
  Event,
  Description,
  Category,
  People,
  Image as ImageIcon,
  ArrowBack,
  ArrowForward,
  Add as AddIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  AttachMoney,
} from '@mui/icons-material';
import { createEvent, getOrganizerEvents, getEventById } from '../services/eventService';
import { useAuth } from '../contexts/AuthContext';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MAPBOX_ACCESS_TOKEN } from '../config/mapbox';
import { debounce } from 'lodash';

const steps = ['Basic Info', 'Location', 'Details', 'Setup & Requirements'];

const categories = [
  { id: 1, name: 'Food' },
  { id: 2, name: 'Crafts' },
  { id: 3, name: 'Art' },
  { id: 4, name: 'Music' },
  { id: 5, name: 'Other' }
];

const CreateEvent = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();
  const mapContainer = useRef(null);
  const map = useRef(null);
  const marker = useRef(null);
  const fileInputRef = useRef(null);
  const templateEventFromState = location.state?.templateEvent;
  const [selectedTemplateEvent, setSelectedTemplateEvent] = useState(templateEventFromState || null);
  const [activeStep, setActiveStep] = useState(0);
  
  const isOrganizer = Number(currentUser?.role_id) === 2;
  
  // Fetch organizer profile to verify ownership
  const { data: organizerProfile } = useQuery(
    ['organizerProfile'],
    async () => {
      const token = localStorage.getItem('token');
      const { apiWithAuth } = await import('../services/api');
      const resp = await apiWithAuth(token).get('/organizer/profile');
      return resp.data;
    },
    { enabled: isOrganizer, retry: false }
  );
  
  // Fetch organizer's events for template selection
  // Backend already filters to only return events for the current organizer
  const { data: organizerEvents = [], isLoading: eventsLoading } = useQuery('organizerEvents', getOrganizerEvents, {
    enabled: isOrganizer, // Only fetch if user is an organizer
    retry: false,
  });
  
  // Show all events as potential templates (not just past ones)
  // Filter to ensure only events belonging to the current organizer are shown
  // Sort by date descending (most recent first)
  const templateEvents = useMemo(() => {
    if (!organizerEvents || organizerEvents.length === 0) return [];
    if (!organizerProfile) return []; // Don't show events until we know the organizer ID
    
    // Filter to only show events that belong to the current organizer
    const filtered = organizerEvents.filter(event => {
      // Backend should already filter, but double-check for security
      return event.organizer_id === organizerProfile.organizer_id;
    });
    
    // Sort by date descending (most recent first)
    return filtered.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateB - dateA; // Most recent first
    });
  }, [organizerEvents, organizerProfile]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: '',
    is_permanent: false,  // True for permanent/recurring events
    application_deadline: '',  // Optional deadline for applications
    location: '',
    latitude: '',
    longitude: '',
    capacity: '',
    fee: '',
    category_ids: [],
    images: [],
    // Setup information
    setup_time: '',
    power_availability: '',
    facilities: '',
    // Security & Safety
    security_measures: '',
    insurance_requirements: '',
    safety_guidelines: '',
    // Vendor Requirements
    required_documents: '',
    vendor_requirements: '',
    equipment_needed: '',
    additional_requirements: '',
    cancellation_policy: '',
    refund_policy: '',
    additional_notes: '',
    // Payment information
    payment_instructions: '',
    payment_deadline_days: '7',
  });
  const [tableTiers, setTableTiers] = useState([]);
  const [customCheckboxes, setCustomCheckboxes] = useState([]);
  const [customQuestions, setCustomQuestions] = useState([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [viewport, setViewport] = useState({
    latitude: 51.0447,
    longitude: -114.0719,
    zoom: 11
  });

  // Debounced search function
  const searchAddress = useCallback(
    debounce(async (query) => {
      if (!query.trim()) {
        setSuggestions([]);
        return;
      }

      try {
        const response = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
            query
          )}.json?access_token=${MAPBOX_ACCESS_TOKEN}&country=ca&types=address,place,poi&limit=5`
        );
        const data = await response.json();
        setSuggestions(data.features || []);
      } catch (error) {
        console.error('Error fetching suggestions:', error);
        setSuggestions([]);
      }
    }, 300),
    []
  );

  useEffect(() => {
    // Only initialize map when on the location step and container exists
    if (activeStep !== 1 || !mapContainer.current) return;

    // If map already exists, update viewport and marker
    if (map.current) {
      // Update map center if viewport changed
      map.current.flyTo({
        center: [viewport.longitude, viewport.latitude],
        zoom: viewport.zoom,
        duration: 0
      });

      // Update marker if coordinates exist in formData (from template)
      if (formData.latitude && formData.longitude) {
        // Remove existing marker
        if (marker.current && marker.current.remove) {
          marker.current.remove();
        }
        // Add new marker
        marker.current = new mapboxgl.Marker({
          color: theme.palette.primary.main,
          draggable: true
        })
          .setLngLat([parseFloat(formData.longitude), parseFloat(formData.latitude)])
          .addTo(map.current);

        // Add drag end event listener
        marker.current.on('dragend', () => {
          const newLngLat = marker.current.getLngLat();
          setFormData(prev => ({
            ...prev,
            latitude: newLngLat.lat,
            longitude: newLngLat.lng
          }));
        });
      }
      return;
    }

    try {
      // Set the Mapbox token
      if (!MAPBOX_ACCESS_TOKEN) {
        throw new Error('Mapbox access token is missing. Please add REACT_APP_MAPBOX_ACCESS_TOKEN to your .env file');
      }
      mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;
      
      // Check if container has dimensions
      const container = mapContainer.current;
      if (!container.offsetWidth || !container.offsetHeight) {
        console.error('Map container has no dimensions');
        return;
      }

      // Initialize the map
      map.current = new mapboxgl.Map({
        container: container,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: [viewport.longitude, viewport.latitude],
        zoom: viewport.zoom,
        attributionControl: true
      });

      // Add navigation controls
      map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

      // Wait for the map to load before adding click handler and marker
      map.current.on('load', () => {
        map.current.on('click', handleMapClick);
        // Force a resize to ensure the map renders properly
        map.current.resize();

        // Add marker if coordinates exist in formData (from template)
        if (formData.latitude && formData.longitude) {
          marker.current = new mapboxgl.Marker({
            color: theme.palette.primary.main,
            draggable: true
          })
            .setLngLat([parseFloat(formData.longitude), parseFloat(formData.latitude)])
            .addTo(map.current);

          // Add drag end event listener
          marker.current.on('dragend', () => {
            const newLngLat = marker.current.getLngLat();
            setFormData(prev => ({
              ...prev,
              latitude: newLngLat.lat,
              longitude: newLngLat.lng
            }));
          });
        }
      });

      // Add error handling
      map.current.on('error', (e) => {
        console.error('Mapbox error:', e);
      });

    } catch (error) {
      console.error('Error initializing map:', error);
    }

    // Cleanup function
    return () => {
      // Remove marker if it exists
      if (marker.current && marker.current.remove) {
        marker.current.remove();
      }
      marker.current = null;

      // Remove the map instance
      if (map.current && map.current.remove) {
        map.current.remove();
      }
      map.current = null;
    };
  }, [activeStep, viewport, formData.latitude, formData.longitude]);

  const handleMapClick = (e) => {
    if (!map.current) return;

    const { lng, lat } = e.lngLat;
    
    // Remove existing marker if it exists
    if (marker.current) {
      marker.current.remove();
    }

    // Create a new marker
    marker.current = new mapboxgl.Marker({
      color: theme.palette.primary.main,
      draggable: true
    })
      .setLngLat([lng, lat])
      .addTo(map.current);

    // Update form data with new coordinates
    setFormData(prev => ({
      ...prev,
      latitude: lat,
      longitude: lng
    }));

    // Add drag end event listener
    marker.current.on('dragend', () => {
      const newLngLat = marker.current.getLngLat();
      setFormData(prev => ({
        ...prev,
        latitude: newLngLat.lat,
        longitude: newLngLat.lng
      }));
    });

    // Fly to the clicked location
    map.current.flyTo({
      center: [lng, lat],
      zoom: 14,
      duration: 1500
    });
  };

  const handleLocationChange = (e) => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      location: value
    }));
    setShowSuggestions(true);
    searchAddress(value);
  };

  const handleSuggestionClick = (suggestion) => {
    const [longitude, latitude] = suggestion.center;
    setFormData(prev => ({
      ...prev,
      location: suggestion.place_name,
      latitude,
      longitude
    }));
    setShowSuggestions(false);
    
    // Update map view
    if (map.current) {
      map.current.flyTo({
        center: [longitude, latitude],
        zoom: 14,
        duration: 1500
      });

      // Update marker
      if (marker.current) {
        marker.current.remove();
      }
      marker.current = new mapboxgl.Marker({
        color: theme.palette.primary.main,
        draggable: true
      })
        .setLngLat([longitude, latitude])
        .addTo(map.current);

      // Add drag end event listener
      marker.current.on('dragend', () => {
        const newLngLat = marker.current.getLngLat();
        setFormData(prev => ({
          ...prev,
          latitude: newLngLat.lat,
          longitude: newLngLat.lng
        }));
      });
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = files.filter(file => 
      file.type.startsWith('image/') && file.size <= 5 * 1024 * 1024 // 5MB limit
    );

    if (validFiles.length === 0) {
      setErrorMessage('Please select valid image files (max 5MB each)');
      return;
    }

    setFormData(prev => ({
      ...prev,
      images: [...prev.images, ...validFiles.map(file => ({
        file,
        preview: URL.createObjectURL(file),
        isMain: prev.images.length === 0
      }))]
    }));
  };

  const handleRemoveImage = (index) => {
    setFormData(prev => {
      const newImages = [...prev.images];
      URL.revokeObjectURL(newImages[index].preview); // Clean up the preview URL
      newImages.splice(index, 1);
      return {
        ...prev,
        images: newImages
      };
    });
  };

  const handleSetMainImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.map((img, i) => ({
        ...img,
        isMain: i === index
      }))
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCategoryChange = (event) => {
    const { value } = event.target;
    setFormData((prev) => ({
      ...prev,
      category_ids: value,
    }));
  };

  // Table Tiers handlers
  const addTableTier = () => {
    // Generate sequential ID starting from 1
    const maxId = tableTiers.length > 0 ? Math.max(...tableTiers.map(t => t.id || 0)) : 0;
    const newTier = {
      id: maxId + 1,
      name: '',
      fee: 0,
      description: '',
      capacity: null  // Optional capacity limit for this tier
    };
    setTableTiers([...tableTiers, newTier]);
  };

  const updateTableTier = (index, field, value) => {
    const updated = [...tableTiers];
    if (field === 'fee') {
      updated[index][field] = parseFloat(value) || 0;
    } else if (field === 'capacity') {
      // Allow empty string or null for unlimited capacity
      updated[index][field] = value === '' || value === null ? null : parseInt(value, 10) || null;
    } else {
      updated[index][field] = value;
    }
    setTableTiers(updated);
  };

  const removeTableTier = (index) => {
    setTableTiers(tableTiers.filter((_, i) => i !== index));
  };

  // Custom Questions handlers
  const addCustomQuestion = () => {
    const maxId = customQuestions.length > 0 ? Math.max(...customQuestions.map(q => q.id || 0)) : 0;
    const newQuestion = {
      id: maxId + 1,
      question: '',
      type: 'text', // 'text', 'checkbox', 'options'
      options: [], // For 'options' type
      required: false
    };
    setCustomQuestions([...customQuestions, newQuestion]);
  };

  const updateCustomQuestion = (index, field, value) => {
    const updated = [...customQuestions];
    updated[index][field] = value;
    setCustomQuestions(updated);
  };

  const removeCustomQuestion = (index) => {
    setCustomQuestions(customQuestions.filter((_, i) => i !== index));
  };

  const addQuestionOption = (questionIndex) => {
    const updated = [...customQuestions];
    if (!updated[questionIndex].options) {
      updated[questionIndex].options = [];
    }
    updated[questionIndex].options.push('');
    setCustomQuestions(updated);
  };

  const updateQuestionOption = (questionIndex, optionIndex, value) => {
    const updated = [...customQuestions];
    updated[questionIndex].options[optionIndex] = value;
    setCustomQuestions(updated);
  };

  const removeQuestionOption = (questionIndex, optionIndex) => {
    const updated = [...customQuestions];
    updated[questionIndex].options.splice(optionIndex, 1);
    setCustomQuestions(updated);
  };

  // Custom Checkboxes handlers
  const addCustomCheckbox = () => {
    // Generate sequential ID starting from 1
    const maxId = customCheckboxes.length > 0 ? Math.max(...customCheckboxes.map(cb => cb.id || 0)) : 0;
    const newCheckbox = {
      id: maxId + 1,
      label: ''
    };
    setCustomCheckboxes([...customCheckboxes, newCheckbox]);
  };

  const updateCustomCheckbox = (index, label) => {
    const updated = [...customCheckboxes];
    updated[index].label = label;
    setCustomCheckboxes(updated);
  };

  const removeCustomCheckbox = (index) => {
    setCustomCheckboxes(customCheckboxes.filter((_, i) => i !== index));
  };

  const createEventMutation = useMutation(
    async (eventData) => {
      try {
        // Create FormData to handle file uploads
        const formDataToSend = new FormData();
        formDataToSend.append('title', eventData.title);
        formDataToSend.append('description', eventData.description);
        formDataToSend.append('is_permanent', eventData.is_permanent ? 'true' : 'false');
        if (eventData.date && eventData.date !== '') {
          formDataToSend.append('date', new Date(eventData.date).toISOString());
        }
        if (eventData.application_deadline && eventData.application_deadline !== '') {
          formDataToSend.append('application_deadline', new Date(eventData.application_deadline).toISOString());
        }
        formDataToSend.append('location', eventData.location);
        formDataToSend.append('latitude', parseFloat(eventData.latitude));
        formDataToSend.append('longitude', parseFloat(eventData.longitude));
        formDataToSend.append('capacity', parseInt(eventData.capacity, 10));
        if (eventData.fee && eventData.fee !== '') {
          formDataToSend.append('fee', parseFloat(eventData.fee));
        }

        // Append setup information
        if (eventData.setup_time) formDataToSend.append('setup_time', eventData.setup_time);
        if (eventData.power_availability) formDataToSend.append('power_availability', eventData.power_availability);
        if (eventData.facilities) formDataToSend.append('facilities', eventData.facilities);
        
        // Append security & safety
        if (eventData.security_measures) formDataToSend.append('security_measures', eventData.security_measures);
        if (eventData.insurance_requirements) formDataToSend.append('insurance_requirements', eventData.insurance_requirements);
        if (eventData.safety_guidelines) formDataToSend.append('safety_guidelines', eventData.safety_guidelines);
        
        // Append vendor requirements
        if (eventData.required_documents) formDataToSend.append('required_documents', eventData.required_documents);
        if (eventData.vendor_requirements) formDataToSend.append('vendor_requirements', eventData.vendor_requirements);
        if (eventData.equipment_needed) formDataToSend.append('equipment_needed', eventData.equipment_needed);
        if (eventData.additional_requirements) formDataToSend.append('additional_requirements', eventData.additional_requirements);
        if (eventData.cancellation_policy) formDataToSend.append('cancellation_policy', eventData.cancellation_policy);
        if (eventData.refund_policy) formDataToSend.append('refund_policy', eventData.refund_policy);
        if (eventData.additional_notes) formDataToSend.append('additional_notes', eventData.additional_notes);
        
        // Append payment information
        if (eventData.payment_instructions) formDataToSend.append('payment_instructions', eventData.payment_instructions);
        if (eventData.payment_deadline_days) formDataToSend.append('payment_deadline_days', parseInt(eventData.payment_deadline_days, 10));

        // Append table tiers if any
        if (tableTiers.length > 0) {
          formDataToSend.append('table_tiers', JSON.stringify(tableTiers));
        }
        
        // Append custom checkboxes if any
        if (customCheckboxes.length > 0) {
          formDataToSend.append('custom_checkboxes', JSON.stringify(customCheckboxes));
        }
        
        // Append custom questions if any
        if (customQuestions.length > 0) {
          formDataToSend.append('custom_questions', JSON.stringify(customQuestions));
        }

        // Append each category ID
        eventData.category_ids.forEach(categoryId => {
          formDataToSend.append('category_ids[]', categoryId);
        });

        // Append the main image (first image or the one marked as main)
        const mainImage = eventData.images.find(img => img.isMain) || eventData.images[0];
        if (mainImage) {
          formDataToSend.append('image', mainImage.file);
        }
        
        console.log('Sending data:', formDataToSend);
        const response = await createEvent(formDataToSend);
        return response;
      } catch (error) {
        console.error('API Error Details:', {
          response: error.response?.data,
          status: error.response?.status,
          message: error.message
        });
        
        if (error.response?.status === 422) {
          const validationErrors = error.response.data.detail;
          if (Array.isArray(validationErrors)) {
            const errorMessages = validationErrors.map(err => 
              `${err.loc[1]}: ${err.msg}`
            ).join('\n');
            throw new Error(errorMessages);
          }
        }

        // Do not redirect to login here; rely on 401 to route elsewhere
        
        throw new Error(error.response?.data?.detail || error.message || 'Failed to create event');
      }
    },
    {
      onSuccess: () => {
        setShowSuccess(true);
        setErrorMessage('');
        // Invalidate events query to refresh the event list
        queryClient.invalidateQueries('events');
        setTimeout(() => {
          history.push('/my-events');
        }, 2000);
      },
      onError: (error) => {
        setErrorMessage(error.message);
      }
    }
  );

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      handleSubmit();
    } else {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setErrorMessage('');
    
    // Validate required fields (date is optional for permanent events)
    if (!formData.title || !formData.description || 
        !formData.location || !formData.capacity || !formData.category_ids ||
        !formData.latitude || !formData.longitude) {
      setErrorMessage('Please fill in all required fields and select a location on the map');
      return;
    }
    
    // Date is required for non-permanent events
    if (!formData.is_permanent && !formData.date) {
      setErrorMessage('Event date is required for non-permanent events');
      return;
    }

    try {
      await createEventMutation.mutateAsync(formData);
    } catch (error) {
      console.error('Error creating event:', error);
    }
  };

  // Populate form from template event
  useEffect(() => {
    if (selectedTemplateEvent) {
      // Parse table_tiers if it's a string
      let parsedTableTiers = [];
      if (selectedTemplateEvent.table_tiers) {
        try {
          parsedTableTiers = typeof selectedTemplateEvent.table_tiers === 'string' 
            ? JSON.parse(selectedTemplateEvent.table_tiers) 
            : selectedTemplateEvent.table_tiers;
        } catch (e) {
          console.error('Error parsing table_tiers:', e);
        }
      }

      // Parse custom_checkboxes if it's a string
      let parsedCustomCheckboxes = [];
      if (selectedTemplateEvent.custom_checkboxes) {
        try {
          parsedCustomCheckboxes = typeof selectedTemplateEvent.custom_checkboxes === 'string'
            ? JSON.parse(selectedTemplateEvent.custom_checkboxes)
            : selectedTemplateEvent.custom_checkboxes;
        } catch (e) {
          console.error('Error parsing custom_checkboxes:', e);
        }
      }

      // Parse custom_questions if it's a string
      let parsedCustomQuestions = [];
      if (selectedTemplateEvent.custom_questions) {
        try {
          parsedCustomQuestions = typeof selectedTemplateEvent.custom_questions === 'string'
            ? JSON.parse(selectedTemplateEvent.custom_questions)
            : selectedTemplateEvent.custom_questions;
        } catch (e) {
          console.error('Error parsing custom_questions:', e);
        }
      }

      // Parse category_ids if it's a string or single value
      let categoryIds = [];
      if (selectedTemplateEvent.category_id) {
        categoryIds = [selectedTemplateEvent.category_id];
      } else if (selectedTemplateEvent.category_ids) {
        categoryIds = Array.isArray(selectedTemplateEvent.category_ids) 
          ? selectedTemplateEvent.category_ids 
          : (typeof selectedTemplateEvent.category_ids === 'string' ? JSON.parse(selectedTemplateEvent.category_ids) : []);
      }

      // Set form data
      setFormData(prev => ({
        ...prev,
        title: selectedTemplateEvent.title || '',
        description: selectedTemplateEvent.description || '',
        date: '', // Don't copy date - let them set a new one
        is_permanent: selectedTemplateEvent.is_permanent || false,
        application_deadline: '', // Don't copy deadline - let them set a new one
        location: selectedTemplateEvent.location || '',
        latitude: selectedTemplateEvent.latitude?.toString() || '',
        longitude: selectedTemplateEvent.longitude?.toString() || '',
        capacity: selectedTemplateEvent.capacity?.toString() || '',
        fee: selectedTemplateEvent.fee?.toString() || '',
        category_ids: categoryIds,
        setup_time: selectedTemplateEvent.setup_time || '',
        power_availability: selectedTemplateEvent.power_availability || '',
        facilities: selectedTemplateEvent.facilities || '',
        security_measures: selectedTemplateEvent.security_measures || '',
        insurance_requirements: selectedTemplateEvent.insurance_requirements || '',
        safety_guidelines: selectedTemplateEvent.safety_guidelines || '',
        required_documents: selectedTemplateEvent.required_documents || '',
        vendor_requirements: selectedTemplateEvent.vendor_requirements || '',
        equipment_needed: selectedTemplateEvent.equipment_needed || '',
        additional_requirements: selectedTemplateEvent.additional_requirements || '',
        cancellation_policy: selectedTemplateEvent.cancellation_policy || '',
        refund_policy: selectedTemplateEvent.refund_policy || '',
        additional_notes: selectedTemplateEvent.additional_notes || '',
        payment_instructions: selectedTemplateEvent.payment_instructions || '',
        payment_deadline_days: selectedTemplateEvent.payment_deadline_days?.toString() || '7',
        images: [], // Don't copy images - they need to upload new ones
      }));

      // Set tiers, checkboxes, and questions
      setTableTiers(parsedTableTiers || []);
      setCustomCheckboxes(parsedCustomCheckboxes || []);
      setCustomQuestions(parsedCustomQuestions || []);

      // Update viewport if coordinates exist
      if (selectedTemplateEvent.latitude && selectedTemplateEvent.longitude) {
        setViewport({
          latitude: parseFloat(selectedTemplateEvent.latitude),
          longitude: parseFloat(selectedTemplateEvent.longitude),
          zoom: 11
        });
      }
    }
  }, [selectedTemplateEvent]);

  // Cleanup preview URLs when component unmounts
  useEffect(() => {
    return () => {
      formData.images.forEach(image => {
        if (image.preview) {
          URL.revokeObjectURL(image.preview);
        }
      });
  };
  }, [formData.images]);

  const getStepContent = (step) => {
    console.log('getStepContent called with step:', step, 'type:', typeof step, 'steps.length:', steps.length);
    // Ensure step is a number
    const stepNum = typeof step === 'number' ? step : parseInt(step, 10);
    switch (stepNum) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label="Event Title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Event />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                multiline
                rows={4}
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Description />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.is_permanent}
                    onChange={(e) => {
                      setFormData(prev => ({
                        ...prev,
                        is_permanent: e.target.checked,
                        date: e.target.checked ? '' : prev.date,  // Clear date if making permanent
                        application_deadline: e.target.checked ? '' : prev.application_deadline,  // Clear deadline if making permanent
                      }));
                    }}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      Permanent/Recurring Event
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Check this for year-round events (e.g., permanent farmer's market). Date and application deadline are not required.
                    </Typography>
                  </Box>
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required={!formData.is_permanent}
                fullWidth
                type="datetime-local"
                label={formData.is_permanent ? "Event Date (Optional - for recurring events)" : "Event Date"}
                name="date"
                value={formData.date}
                onChange={handleChange}
                disabled={formData.is_permanent}
                InputLabelProps={{
                  shrink: true,
                }}
                variant="outlined"
                helperText={formData.is_permanent ? "Leave empty for permanent events" : "Required for one-time events"}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Application Deadline (Optional)"
                name="application_deadline"
                value={formData.application_deadline}
                onChange={handleChange}
                disabled={formData.is_permanent}
                InputLabelProps={{
                  shrink: true,
                }}
                variant="outlined"
                helperText={formData.is_permanent ? "Not applicable for permanent events" : "Set a deadline for vendors to apply. Must be before the event date. Leave empty to allow applications until the event date."}
              />
            </Grid>
          </Grid>
        );
      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box sx={{ position: 'relative' }}>
                <TextField
                  required
                  fullWidth
                  label="Location"
                  name="location"
                  value={formData.location}
                  onChange={handleLocationChange}
                  onFocus={() => setShowSuggestions(true)}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOn />
                      </InputAdornment>
                    ),
                  }}
                  helperText="Start typing to search for locations"
                />
                {showSuggestions && suggestions.length > 0 && (
                  <Paper
                    sx={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      zIndex: 2,
                      mt: 1,
                      maxHeight: 300,
                      overflow: 'auto',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      bgcolor: 'background.paper'
                    }}
                  >
                    <List>
                      {suggestions.map((suggestion, index) => (
                        <ListItem
                          key={index}
                          button
                          onClick={() => handleSuggestionClick(suggestion)}
                          sx={{
                            '&:hover': {
                              bgcolor: 'action.hover'
                            }
                          }}
                        >
                          <ListItemIcon>
                            <LocationOn color="primary" />
                          </ListItemIcon>
                          <ListItemText
                            primary={suggestion.text}
                            secondary={suggestion.place_name}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                )}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Card 
                sx={{ 
                  height: { xs: '300px', md: '400px' }, 
                  overflow: 'hidden', 
                  borderRadius: 2,
                  position: 'relative',
                  bgcolor: 'background.paper'
                }}
              >
                <Box
                  ref={mapContainer}
                  sx={{
                    width: '100%',
                    height: '100%',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    bgcolor: 'background.paper'
                  }}
                />
                {formData.latitude && formData.longitude && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 16,
                      left: 16,
                      bgcolor: 'rgba(255, 255, 255, 0.9)',
                      p: 1.5,
                      borderRadius: 1,
                      backdropFilter: 'blur(8px)',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Selected Location:
                    </Typography>
                    <Typography variant="body2">
                      {parseFloat(formData.latitude || 0).toFixed(6)}, {parseFloat(formData.longitude || 0).toFixed(6)}
                    </Typography>
                  </Box>
                )}
              </Card>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Click on the map to set the event location. You can drag the marker to adjust the position.
              </Typography>
            </Grid>
          </Grid>
        );
      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                type="number"
                label="Vendor Capacity"
                name="capacity"
                value={formData.capacity}
                onChange={handleChange}
                inputProps={{ min: 1 }}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <People />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <AttachMoney /> Vendor Fee Tiers (Optional)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Create different pricing tiers for vendors (e.g., Premium Booth, Standard Booth, Basic Table). 
                Vendors will select which tier they want when applying. You can set a capacity limit for each tier (e.g., 30 outdoor spaces, 50 indoor spaces). 
                If you don't create tiers, you can set a single fee below.
              </Typography>
              {tableTiers.map((tier, index) => (
                <Card key={tier.id} sx={{ mb: 2, p: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label="Tier Name"
                        value={tier.name}
                        onChange={(e) => updateTableTier(index, 'name', e.target.value)}
                        placeholder="e.g., Premium Booth"
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <TextField
                        fullWidth
                        label="Fee ($)"
                        type="number"
                        value={tier.fee}
                        onChange={(e) => updateTableTier(index, 'fee', e.target.value)}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <TextField
                        fullWidth
                        label="Capacity (Optional)"
                        type="number"
                        value={tier.capacity || ''}
                        onChange={(e) => updateTableTier(index, 'capacity', e.target.value)}
                        placeholder="Unlimited"
                        inputProps={{ min: 1 }}
                        size="small"
                        helperText="Max spots for this tier"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Description (Optional)"
                        value={tier.description}
                        onChange={(e) => updateTableTier(index, 'description', e.target.value)}
                        placeholder="e.g., Prime location, 10x10 space"
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={1}>
                      <IconButton
                        color="error"
                        onClick={() => removeTableTier(index)}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Card>
              ))}
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addTableTier}
                sx={{ mt: 1 }}
              >
                Add Tier
              </Button>
              {tableTiers.length === 0 && (
                <Box sx={{ mt: 3 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Single Vendor Fee (Optional)"
                    name="fee"
                    value={formData.fee}
                    onChange={handleChange}
                    inputProps={{ min: 0, step: 0.01 }}
                    variant="outlined"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AttachMoney />
                        </InputAdornment>
                      ),
                    }}
                    helperText="Enter the fee vendors need to pay to participate"
                  />
                </Box>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required variant="outlined">
                <InputLabel>Categories</InputLabel>
                <Select
                  multiple
                  name="category_ids"
                  value={formData.category_ids}
                  onChange={handleCategoryChange}
                  label="Categories"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const category = categories.find(cat => cat.id === value);
                        return (
                          <Chip 
                            key={value} 
                            label={category ? category.name : value}
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  )}
                  startAdornment={
                    <InputAdornment position="start">
                      <Category />
                    </InputAdornment>
                  }
                >
                  <MenuItem value={1}>Food</MenuItem>
                  <MenuItem value={2}>Crafts</MenuItem>
                  <MenuItem value={3}>Art</MenuItem>
                  <MenuItem value={4}>Music</MenuItem>
                  <MenuItem value={5}>Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Card sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ImageIcon /> Event Images
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Upload multiple images for your event. The first image will be used as the cover image.
                  Maximum file size: 5MB per image.
                </Typography>
                
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: 'none' }}
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                />
                
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Button
                    variant="contained"
                    onClick={() => fileInputRef.current?.click()}
                    startIcon={<CloudUploadIcon />}
                    sx={{ minWidth: '200px' }}
                  >
                    Upload Images
                  </Button>
                </Box>

                {formData.images.length > 0 && (
                  <ImageList sx={{ width: '100%', height: 200 }} cols={3} rowHeight={164}>
                    {formData.images.map((image, index) => (
                      <ImageListItem key={index}>
                        <img
                          src={image.preview}
                          alt={`Event image ${index + 1}`}
                          loading="lazy"
                          style={{ height: '100%', objectFit: 'cover' }}
                        />
                        <ImageListItemBar
                          sx={{
                            background:
                              'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, ' +
                              'rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)',
                          }}
                          position="top"
                          actionIcon={
                            <IconButton
                              sx={{ color: 'white' }}
                              onClick={() => handleRemoveImage(index)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          }
                          actionPosition="right"
                        />
                        <ImageListItemBar
                          title={image.isMain ? "Main Image" : "Click to set as main"}
                          position="bottom"
                          actionIcon={
                            !image.isMain && (
                              <IconButton
                                sx={{ color: 'white' }}
                                onClick={() => handleSetMainImage(index)}
                              >
                                <ImageIcon />
                              </IconButton>
                            )
                          }
                          actionPosition="right"
                        />
                      </ImageListItem>
                    ))}
                  </ImageList>
                )}
              </Card>
            </Grid>
          </Grid>
        );
      case 3:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                Setup Information
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Setup Time"
                name="setup_time"
                value={formData.setup_time}
                onChange={handleChange}
                variant="outlined"
                placeholder="e.g., 6:00 AM - 8:00 AM"
                helperText="When vendors should arrive for setup"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Power Availability"
                name="power_availability"
                value={formData.power_availability}
                onChange={handleChange}
                variant="outlined"
                placeholder="e.g., Limited outlets available, Bring extension cords"
                helperText="Information about electrical power access"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Facilities"
                name="facilities"
                value={formData.facilities}
                onChange={handleChange}
                variant="outlined"
                placeholder="e.g., Restrooms, Parking, Food vendors on site"
                helperText="Available facilities at the event location"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Security & Safety
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Security Measures"
                name="security_measures"
                value={formData.security_measures}
                onChange={handleChange}
                variant="outlined"
                placeholder="e.g., On-site security personnel, CCTV monitoring"
                helperText="Security measures in place"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Insurance Requirements"
                name="insurance_requirements"
                value={formData.insurance_requirements}
                onChange={handleChange}
                variant="outlined"
                placeholder="e.g., General liability insurance required, Minimum $1M coverage"
                helperText="Insurance requirements for vendors"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Safety Guidelines"
                name="safety_guidelines"
                value={formData.safety_guidelines}
                onChange={handleChange}
                variant="outlined"
                placeholder="e.g., Follow fire safety protocols, Keep aisles clear"
                helperText="Important safety guidelines"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Vendor Requirements
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Required Documents"
                name="required_documents"
                value={formData.required_documents}
                onChange={handleChange}
                variant="outlined"
                placeholder="e.g., Business license, Food handler permit, Insurance certificate"
                helperText="Documents vendors must provide"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Additional Vendor Requirements"
                name="vendor_requirements"
                value={formData.vendor_requirements}
                onChange={handleChange}
                variant="outlined"
                placeholder="e.g., Must bring own tables, Tents must be weighted, No amplified sound, Setup must be complete by 8 AM, Breakdown cannot start before 5 PM"
                helperText="Additional requirements, restrictions, or important information for vendors"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Equipment Needed"
                name="equipment_needed"
                value={formData.equipment_needed}
                onChange={handleChange}
                variant="outlined"
                placeholder="e.g., Vendors must bring their own tables, chairs, display stands"
                helperText="Equipment vendors need to bring"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Cancellation Policy"
                name="cancellation_policy"
                value={formData.cancellation_policy}
                onChange={handleChange}
                variant="outlined"
                placeholder="e.g., Cancellations must be made 7 days in advance for full refund"
                helperText="Cancellation policy for vendors"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Refund Policy"
                name="refund_policy"
                value={formData.refund_policy}
                onChange={handleChange}
                variant="outlined"
                placeholder="e.g., Refunds available up to 7 days before event, 50% refund within 3 days"
                helperText="Refund policy for vendors"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Additional Notes"
                name="additional_notes"
                value={formData.additional_notes}
                onChange={handleChange}
                variant="outlined"
                placeholder="Any other important information vendors should know"
                helperText="Miscellaneous notes or special instructions"
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" gutterBottom>
                Payment Information
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Configure how vendors should pay their fees. This information will be sent in payment reminders.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Payment Instructions"
                name="payment_instructions"
                value={formData.payment_instructions}
                onChange={handleChange}
                variant="outlined"
                placeholder="e.g., Send payment via e-transfer to payments@example.com, or mail check to [Address]. Include your business name in the memo."
                helperText="Provide clear instructions on how vendors should submit their payment. This will be included in payment reminder notifications."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Payment Deadline (Days)"
                name="payment_deadline_days"
                value={formData.payment_deadline_days}
                onChange={handleChange}
                inputProps={{ min: 1, max: 30 }}
                variant="outlined"
                helperText="Number of days after acceptance that vendors have to pay (default: 7)"
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 2 }}>
                Custom Checkboxes (Optional)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Add custom checkboxes that vendors can select when applying (e.g., "Has insurance", "Accepts credit cards").
              </Typography>
              {customCheckboxes.map((checkbox, index) => (
                <Card key={checkbox.id} sx={{ mb: 2, p: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={11}>
                      <TextField
                        fullWidth
                        label="Checkbox Label"
                        value={checkbox.label}
                        onChange={(e) => updateCustomCheckbox(index, e.target.value)}
                        placeholder="e.g., Has general liability insurance"
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={1}>
                      <IconButton
                        color="error"
                        onClick={() => removeCustomCheckbox(index)}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Card>
              ))}
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addCustomCheckbox}
                sx={{ mt: 1 }}
              >
                Add Checkbox
              </Button>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 2 }}>
                Custom Questionnaire (Optional)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Create custom questions for vendors to answer when applying. Use this to filter and evaluate vendors based on your specific market needs.
              </Typography>
              {customQuestions.map((question, index) => (
                <Card key={question.id} sx={{ mb: 2, p: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Question"
                        value={question.question}
                        onChange={(e) => updateCustomQuestion(index, 'question', e.target.value)}
                        placeholder="e.g., How many years have you been in business?"
                        size="small"
                        sx={{ mb: 2 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Question Type</InputLabel>
                        <Select
                          value={question.type}
                          onChange={(e) => updateCustomQuestion(index, 'type', e.target.value)}
                          label="Question Type"
                        >
                          <MenuItem value="text">Text Field (Open Answer)</MenuItem>
                          <MenuItem value="checkbox">Checkboxes (Multiple Selection)</MenuItem>
                          <MenuItem value="options">Options (Single Selection)</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={question.required || false}
                            onChange={(e) => updateCustomQuestion(index, 'required', e.target.checked)}
                          />
                        }
                        label="Required"
                      />
                    </Grid>
                    {(question.type === 'options' || question.type === 'checkbox') && (
                      <Grid item xs={12}>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                          Options:
                        </Typography>
                        {question.options && question.options.map((option, optIndex) => (
                          <Box key={optIndex} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                            <TextField
                              fullWidth
                              size="small"
                              value={option}
                              onChange={(e) => updateQuestionOption(index, optIndex, e.target.value)}
                              placeholder={`Option ${optIndex + 1}`}
                            />
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => removeQuestionOption(index, optIndex)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        ))}
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={() => addQuestionOption(index)}
                          sx={{ mt: 1 }}
                        >
                          Add Option
                        </Button>
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <IconButton
                          color="error"
                          onClick={() => removeCustomQuestion(index)}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Grid>
                  </Grid>
                </Card>
              ))}
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addCustomQuestion}
                sx={{ mt: 1 }}
              >
                Add Question
              </Button>
            </Grid>
          </Grid>
        );
      default:
        console.error('Unknown step:', step);
        return `Unknown step: ${step}`;
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      {/* Template Selector */}
      {isOrganizer && (
        <Paper elevation={2} sx={{ p: 2.5, mb: 3, bgcolor: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                Use an Existing Event as Template
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Select a previous event to copy its settings and details
              </Typography>
            </Box>
            {selectedTemplateEvent && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => setSelectedTemplateEvent(null)}
              >
                Clear Template
              </Button>
            )}
          </Box>
          {eventsLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Loading your events...
              </Typography>
            </Box>
          ) : templateEvents.length > 0 ? (
            <FormControl fullWidth>
              <Select
                value={selectedTemplateEvent?.event_id || ''}
                onChange={async (e) => {
                  const eventId = e.target.value;
                  if (!eventId) {
                    setSelectedTemplateEvent(null);
                    return;
                  }
                  // Fetch full event details to get all fields (tiers, checkboxes, questions, etc.)
                  try {
                    const fullEvent = await getEventById(eventId);
                    
                    // Verify that this event belongs to the current organizer
                    if (organizerProfile && fullEvent.organizer_id !== organizerProfile.organizer_id) {
                      console.error('Security: Event does not belong to current organizer');
                      setSelectedTemplateEvent(null);
                      setErrorMessage('You can only use your own events as templates.');
                      return;
                    }
                    
                    setSelectedTemplateEvent(fullEvent);
                  } catch (error) {
                    console.error('Error fetching event details:', error);
                    // Fallback to basic event from list (but verify ownership)
                    const event = templateEvents.find(ev => ev.event_id === eventId);
                    if (event && organizerProfile && event.organizer_id !== organizerProfile.organizer_id) {
                      console.error('Security: Event does not belong to current organizer');
                      setSelectedTemplateEvent(null);
                      setErrorMessage('You can only use your own events as templates.');
                      return;
                    }
                    setSelectedTemplateEvent(event || null);
                  }
                }}
                displayEmpty
                sx={{ bgcolor: 'background.paper' }}
              >
                <MenuItem value="">
                  <em>Start from scratch</em>
                </MenuItem>
                {templateEvents.map((event) => {
                  const eventDate = new Date(event.date);
                  const isPast = eventDate < new Date();
                  return (
                    <MenuItem key={event.event_id} value={event.event_id}>
                      {event.title} - {eventDate.toLocaleDateString()} {isPast ? '(Past)' : '(Upcoming)'}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              You don't have any events yet. Once you create your first event, you can use it as a template for future events.
            </Typography>
          )}
          {selectedTemplateEvent && (
            <Alert 
              severity="info" 
              sx={{ mt: 2 }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Using template: "{selectedTemplateEvent.title}"
              </Typography>
              <Typography variant="caption" color="text.secondary">
                All fields have been pre-filled. You can edit anything before creating the new event. The date and images have been cleared.
              </Typography>
            </Alert>
          )}
        </Paper>
      )}
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4,
          borderRadius: 2,
          background: `linear-gradient(to bottom right, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.light, 0.1)})`,
        }}
      >
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
            Create New Event
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Fill in the details below to create your event
          </Typography>
        </Box>

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errorMessage}
          </Alert>
        )}

        <Stepper 
          activeStep={activeStep} 
          orientation={isMobile ? "vertical" : "horizontal"}
          sx={{ mb: { xs: 3, md: 4 } }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel sx={{ 
                '& .MuiStepLabel-label': {
                  fontSize: { xs: '0.875rem', md: '1rem' },
                }
              }}>
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        <form onSubmit={handleSubmit}>
          {getStepContent(activeStep)}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: { xs: 3, md: 4 }, gap: 2, flexDirection: { xs: 'column-reverse', md: 'row' } }}>
                <Button
                  variant="outlined"
              onClick={activeStep === 0 ? () => history.push('/my-events') : handleBack}
              startIcon={activeStep === 0 ? <ArrowBack /> : null}
                  disabled={createEventMutation.isLoading}
                  fullWidth={isMobile}
                  size={isMobile ? "large" : "medium"}
                  sx={{
                    minHeight: { xs: 48, md: 40 },
                    fontSize: { xs: '0.9375rem', md: '0.875rem' },
                  }}
                >
              {activeStep === 0 ? 'Cancel' : 'Back'}
                </Button>
                <Button
                  variant="contained"
              onClick={handleNext}
              endIcon={activeStep === steps.length - 1 ? null : <ArrowForward />}
                  disabled={createEventMutation.isLoading}
                  fullWidth={isMobile}
                  size={isMobile ? "large" : "medium"}
                  sx={{
                    minHeight: { xs: 48, md: 40 },
                    fontSize: { xs: '0.9375rem', md: '0.875rem' },
                  }}
                >
              {activeStep === steps.length - 1 ? (
                createEventMutation.isLoading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Create Event'
                )
              ) : (
                'Next'
                  )}
                </Button>
              </Box>
        </form>
      </Paper>

      <Snackbar
        open={showSuccess}
        autoHideDuration={2000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          Event created successfully! Redirecting to dashboard...
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CreateEvent; 